import { useCallback, useEffect, useReducer, useRef } from 'react'
import { useApiClient } from './api-client'
import type {
  SearchRuleViolationsRequest,
  SearchRuleViolationsResponse,
} from '../api/v1/client/services/rule-violations'

export interface UseRuleViolationsProps extends SearchRuleViolationsRequest {
  skip?: boolean
}

export interface UseRuleViolationsResult {
  data: SearchRuleViolationsResponse | null
  loading: boolean
  error: Error | null
  refetch: (
    overrides?: Partial<SearchRuleViolationsRequest>,
  ) => Promise<SearchRuleViolationsResponse | null>
}

interface State {
  data: SearchRuleViolationsResponse | null
  loading: boolean
  error: Error | null
}

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: SearchRuleViolationsResponse }
  | { type: 'FETCH_ERROR'; payload: Error }
  | { type: 'RESET' }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null }
    case 'FETCH_SUCCESS':
      return { data: action.payload, loading: false, error: null }
    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.payload }
    case 'RESET':
      return { data: null, loading: false, error: null }
  }
}

const initialState = (skip: boolean): State => ({
  data: null,
  loading: !skip,
  error: null,
})

/**
 * Retrieves rule violations via POST /rules/violations/search.
 * Re-runs whenever the request body changes (deep-equal by JSON serialization).
 */
export const useRuleViolations = ({
  skip = false,
  ...request
}: UseRuleViolationsProps): UseRuleViolationsResult => {
  const apiClient = useApiClient()
  const [state, dispatch] = useReducer(reducer, skip, initialState)
  const requestKey = JSON.stringify(request)
  const latestRequestId = useRef(0)

  const fetchViolations = useCallback(
    async (
      overrides?: Partial<SearchRuleViolationsRequest>,
    ): Promise<SearchRuleViolationsResponse | null> => {
      const requestId = ++latestRequestId.current
      dispatch({ type: 'FETCH_START' })
      try {
        const merged: SearchRuleViolationsRequest = {
          ...request,
          ...overrides,
          filter: { ...request.filter, ...overrides?.filter },
        }
        const result = await apiClient.ruleViolations.search(merged)
        if (requestId === latestRequestId.current) {
          dispatch({ type: 'FETCH_SUCCESS', payload: result })
        }
        return result
      } catch (err) {
        if (requestId === latestRequestId.current) {
          dispatch({ type: 'FETCH_ERROR', payload: err as Error })
        }
        return null
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- requestKey captures request shape
    },
    [apiClient, requestKey],
  )

  useEffect(() => {
    if (skip) {
      // Invalidate any in-flight request so its late response cannot update state.
      latestRequestId.current += 1
      dispatch({ type: 'RESET' })
      return
    }
    fetchViolations()
  }, [skip, fetchViolations])

  return { ...state, refetch: fetchViolations }
}
